import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Logo from '@/components/navbar/Logo';
import HeroSection from '@/components/HeroSection';
import ProfessionTicker from '@/components/ProfessionTicker';
import MissionSection from '@/components/MissionSection';
import Features from '@/components/Features';
import PracticeShiftSection from '@/components/PracticeShiftSection';
import TargetAudience from '@/components/TargetAudience';
import FaqSection from '@/components/FaqSection';
import MarketingImage from '@/components/landing/MarketingImage';
import { usePublicSeo } from '@/marketing/usePublicSeo';
import { openCookiePreferences } from '@/lib/cookieConsent';
import { recordLandingOperationalLocationVisit } from '@/lib/landingOperations';
import { trackMetaSignupStart } from '@/lib/analytics';
import styles from './StartPage.module.css';

const StartStickyCta: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hero = document.getElementById('start-hero');
    if (!hero) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  return (
    <RouterLink
      to="/signup"
      onClick={trackMetaSignupStart}
      className={`${styles.stickyBar} ${visible ? styles.stickyBarVisible : ''}`}
      aria-label="Get Started for Free"
      data-analytics-event="Landing CTA Clicked"
      data-analytics-id="start_sticky_get_started"
      data-analytics-label="Get Started for Free"
      data-analytics-section="sticky_cta"
    >
      <span className={styles.stickyText}>
        Get Started for Free
      </span>
    </RouterLink>
  );
};

const StartClosingCta: React.FC = () => {
  return (
    <section className={styles.closing} aria-labelledby="start-closing-heading">
      <div className={styles.closingInner}>
        <h2 id="start-closing-heading" className={styles.closingHeadline}>
          More clients. The same care.
          <br />
          More time for the work you love.
        </h2>
        <p className={styles.closingNote}>31 days free. No credit card. Cancel anytime.</p>
        <RouterLink
          to="/signup"
          onClick={trackMetaSignupStart}
          className={styles.closingButton}
          data-analytics-event="Landing CTA Clicked"
          data-analytics-id="start_closing_get_started"
          data-analytics-label="Sign Up"
          data-analytics-section="closing_cta"
        >
          Sign Up
        </RouterLink>
      </div>
    </section>
  );
};

const StartFooter: React.FC = () => {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <RouterLink to="/" className={styles.footerBrand}>
          <MarketingImage
            src="/images/marketing/landing-logo-2.webp"
            alt="CalorieScience"
            className={styles.footerLogo}
          />
        </RouterLink>
        <nav className={styles.footerLinks} aria-label="Legal">
          <RouterLink to="/privacy">Privacy Policy</RouterLink>
          <RouterLink to="/terms">Terms and Conditions</RouterLink>
          <RouterLink to="/cookies">Cookie Policy</RouterLink>
          <button type="button" onClick={openCookiePreferences}>
            Cookie Preferences
          </button>
        </nav>
        <p className={styles.footerCopy}>
          &copy; {new Date().getFullYear()} CalorieScience. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

const StartPage: React.FC = () => {
  usePublicSeo(
    '/start',
    'Scale Your Nutrition Practice | CalorieScience',
    'Scale your nutrition practice with connected client intake, meal planning, biomarkers, and follow-ups. Try CalorieScience free for 31 days. No credit card required.'
  );

  useEffect(() => {
    recordLandingOperationalLocationVisit();
  }, []);

  useEffect(() => {
    const stripHash = () => {
      if (!window.location.hash) return;
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    };
    stripHash();
    window.addEventListener('hashchange', stripHash);
    return () => window.removeEventListener('hashchange', stripHash);
  }, []);

  return (
    <div id="top" className={styles.page}>
      <nav id="site-navigation" aria-label="Main" className="w-full sticky top-0 z-50 bg-white pt-3 md:pt-7">
        <div className="landing-container">
          <div className="rounded-[1.375rem] border border-[#E7EEF2] bg-white px-4 py-3 sm:px-6 sm:py-3.5 shadow-[0_0.75rem_1.875rem_rgba(15,23,42,0.08)]">
            <Logo variant="landing" />
          </div>
        </div>
      </nav>
      <main className={styles.main}>
        <HeroSection variant="start" />
        <ProfessionTicker />
        <section id="about-us" className="scroll-mt-24">
          <MissionSection />
        </section>
        <Features showWorkflowTabs={false} showOutcomeText={false} />
        <PracticeShiftSection />
        <TargetAudience />
        <StartClosingCta />
        <FaqSection />
      </main>
      <StartFooter />
      <StartStickyCta />
    </div>
  );
};

export default StartPage;
